import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { BackButton, NextButton } from "src/components/application/styled-components/Buttons";
import {
  AdhocKycInvalidReason,
  adhocKycErrorMessage,
  useAdhocKycResendMutation,
} from "src/store/features/api/adhocKyc";
import { AdhocKycBody, AdhocKycError, AdhocKycHeading } from "./AdhocKycShell";

/** Server reasons plus the SPA's own "couldn't load" case. */
export type InvalidLinkReason = AdhocKycInvalidReason | "error";

export interface InvalidLinkPageProps {
  reason: InvalidLinkReason;
  /** The expired raw token — enables "Send me a fresh link". */
  token?: string;
  homePath: string;
}

export const INVALID_LINK_COPY: Record<InvalidLinkReason, { title: string; body: string }> = {
  invalid: {
    title: "This link isn't valid",
    body: "Please open the most recent email from Caleb & Brown and use the link in it. If you keep seeing this, contact support.",
  },
  expired: {
    title: "This link has expired",
    body: "For your security, verification links only work for a limited time. We can email you a fresh one.",
  },
  consumed: {
    title: "You've already completed this",
    body: "Thanks — your identity re-verification has been received and there's nothing more to do.",
  },
  revoked: {
    title: "This request is no longer active",
    body: "If you think that's a mistake, please contact support and we'll sort it out.",
  },
  disabled: {
    title: "Not available yet",
    body: "Identity re-verification isn't open for your account yet. We'll email you when it is — nothing to do for now.",
  },
  none: {
    title: "Nothing to complete",
    body: "There's no identity re-verification pending on your account.",
  },
  error: {
    title: "Something went wrong",
    body: "We couldn't load your verification. Please refresh the page to try again.",
  },
};

/** One neutral line whether or not the token was still alive — no enumeration. */
export const RESEND_SENT_MESSAGE = "If that link was still valid we've emailed you a new one.";

/**
 * Terminal page for every "can't proceed" outcome — bad / expired / used /
 * revoked links, a switched-off campaign, a logged-in customer with nothing
 * pending, or a failed context load. Never redirects to login.
 */
export default function InvalidLinkPage({ reason, token, homePath }: InvalidLinkPageProps): JSX.Element {
  const [resend, { isLoading, error }] = useAdhocKycResendMutation();
  const [sent, setSent] = useState(false);
  const copy = INVALID_LINK_COPY[reason];
  const canResend = reason === "expired" && Boolean(token);

  const handleResend = async (): Promise<void> => {
    const result = await resend({ token: token as string });
    if (!("error" in result)) setSent(true);
  };

  return (
    <Box data-testid="adhoc-kyc-invalid-page" data-reason={reason}>
      <AdhocKycHeading>{copy.title}</AdhocKycHeading>
      <AdhocKycBody>{copy.body}</AdhocKycBody>
      {error ? <AdhocKycError message={adhocKycErrorMessage(error)} /> : null}
      {sent ? (
        <Typography role="status" data-testid="adhoc-kyc-resend-sent" sx={{ fontSize: "14px", mb: 2 }}>
          {RESEND_SENT_MESSAGE}
        </Typography>
      ) : null}
      {canResend && !sent ? (
        <NextButton
          fullWidth
          disabled={isLoading}
          onClick={handleResend}
          data-testid="adhoc-kyc-resend"
          sx={{ mb: 1.5 }}>
          Send me a fresh link
        </NextButton>
      ) : null}
      <BackButton fullWidth href={homePath} data-testid="adhoc-kyc-invalid-home">
        Go to Caleb &amp; Brown
      </BackButton>
      <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 3, textAlign: "center" }}>
        Need help?{" "}
        <Button
          component="a"
          href="mailto:support@calebandbrown.com"
          sx={{ textTransform: "none", p: 0, minWidth: 0, fontSize: "13px", verticalAlign: "baseline" }}>
          support@calebandbrown.com
        </Button>
      </Typography>
    </Box>
  );
}
